/**
 * Matomo Cloud analytics
 * EU-hosted, GDPR-compliant, cookieless tracking mode
 */

declare global {
  interface Window {
    _paq?: Array<unknown[]>;
  }
}

const MATOMO_URL = process.env.NEXT_PUBLIC_MATOMO_URL;
const MATOMO_SITE_ID = process.env.NEXT_PUBLIC_MATOMO_SITE_ID;

export function initMatomo(): void {
  if (typeof window === 'undefined' || !MATOMO_URL || !MATOMO_SITE_ID) return;

  window._paq = window._paq || [];
  window._paq.push(['disableCookies']); // Cookieless tracking
  window._paq.push(['trackPageView']);
  window._paq.push(['enableLinkTracking']);
  window._paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
  window._paq.push(['setSiteId', MATOMO_SITE_ID]);

  const script = document.createElement('script');
  script.async = true;
  script.src = `${MATOMO_URL}/matomo.js`;
  document.head.appendChild(script);
}

export function trackEvent(
  category: string,
  action: string,
  name?: string,
  value?: number,
): void {
  if (typeof window === 'undefined' || !window._paq) return;
  window._paq.push(['trackEvent', category, action, name, value]);
}

export function trackPageView(url?: string): void {
  if (typeof window === 'undefined' || !window._paq) return;
  if (url) window._paq.push(['setCustomUrl', url]);
  window._paq.push(['trackPageView']);
}

/**
 * KAN-305: Link Matomo visits to an authenticated user.
 *
 * We pass the Supabase UUID (already pseudonymized).
 * Matomo Cloud's "Pseudonymize User ID" setting hashes it again before storage.
 *
 * NEVER pass email, name, or any PII here.
 */
export function setUserId(userId: string): void {
  if (typeof window === 'undefined' || !window._paq) return;
  window._paq.push(['setUserId', userId]);
  // Re-track the current page so the User ID is associated with this visit
  window._paq.push(['trackPageView']);
}

/**
 * KAN-305: Clear the User ID on logout so subsequent anonymous browsing
 * is not attributed to the previous user.
 *
 * Ref: https://matomo.org/faq/reports/set-up-user-id-tracking-in-matomo/
 */
export function resetUserId(): void {
  if (typeof window === 'undefined' || !window._paq) return;
  window._paq.push(['resetUserId']);
  window._paq.push(['appendToTrackingUrl', 'new_visit=1']);
  window._paq.push(['trackPageView']);
  // Remove the new_visit flag so it doesn't persist on future page views
  window._paq.push(['appendToTrackingUrl', '']);
}
